import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Button, SpinLoader } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import Timer from '../Timer';
import TimeoutModal from '../TimeoutModal';
import PaymentImage from '../../../ui/atoms/PaymentImage';
import useSearchRedirect from '../../../hooks/store/useSearchRedirect';
import 'styles/components/purchase/TicketsPanel';

/**
 * PaypalPaymentInfo component.
 * Render PayPal payment information panel with purchase summary, timer, and confirmation button.
 *
 * @param {object} props - amount, confirmPaypalPayment, departs, from, isRoundTrip, paymentId, to, paymentType, token, timerRemainingTime, isOpenTicket, paymentStatus, purchaseState, purchaseIsExpired, paymentIsLoaded.
 * @param {number} props.amount - Total amount to be paid for the purchase.
 * @param {Function} props.confirmPaypalPayment - Callback function to confirm PayPal payment with token and payment ID.
 * @param {string} props.departs - Departure date/time string for the trip.
 * @param {string} props.from - Origin location name for the trip.
 * @param {boolean} props.isRoundTrip - Indicates if the trip is round trip or one way.
 * @param {number} props.paymentId - Unique identifier for the payment transaction.
 * @param {string} props.to - Destination location name for the trip.
 * @param {string} props.paymentType - Type of payment method (e.g., 'paypal', 'third_party').
 * @param {string} props.token - Payment token required for transaction confirmation.
 * @param {number} props.timerRemainingTime - Remaining time in seconds for payment completion.
 * @param {boolean} props.isOpenTicket - Indicates if the ticket has flexible dates.
 * @param {string} props.paymentStatus - Current status of the payment (optional).
 * @param {string} props.purchaseState - Current state of the purchase process (optional).
 * @param {boolean} props.purchaseIsExpired - Indicates if the purchase has expired (optional).
 * @param {boolean} props.paymentIsLoaded - Indicates if payment information has been loaded (optional).
 * @returns {JSX.Element} The rendered PayPal payment information component.
 */
const PaypalPaymentInfo = ({
  amount,
  confirmPaypalPayment,
  departs,
  from,
  isRoundTrip,
  paymentId,
  to,
  paymentType,
  token,
  timerRemainingTime,
  isOpenTicket,
  paymentStatus,
  purchaseState,
  purchaseIsExpired,
  paymentIsLoaded,
}) => {
  const { t } = useTranslation('general');
  const [isVisible, setIsVisible] = useState(false);
  const departsDisplay = moment(departs).format('LLL');
  const { handleGotoSearch } = useSearchRedirect();

  const rejectedPayment =
    purchaseIsExpired && paymentStatus === 'pending' && purchaseState === 'attempt';

  useEffect(() => {
    if (paymentIsLoaded) {
      // Add a small delay to ensure there's no briefly displayed content
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [paymentIsLoaded]);

  /**
   * Handles redirection to the search page using the useSearchRedirect hook
   */
  const handleRedirectToSearch = () => {
    handleGotoSearch();
  };

  /**
   * Handles PayPal payment confirmation by calling the confirmPaypalPayment function
   */
  const onConfirm = () => {
    confirmPaypalPayment(token, paymentId);
  };

  let buttonText;
  if (paymentType === 'paypal') buttonText = t('payment:button.confirm_and_pay');
  if (paymentType === 'third_party') buttonText = t('payment:button.pay_in_coppel_pay');

  if (!isVisible) {
    return (
      <div className="tickets-panel">
        <div
          className="tickets-panel-content"
          style={{
            minHeight: '200px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <SpinLoader color="primary" size="L" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="tickets-panel">
        <div className="tickets-panel-content">
          <Spacing vertical>
            {paymentType === 'paypal' && <PaymentImage type="paypal-logo" />}
            {paymentType === 'third_party' && <PaymentImage type="coppelpay" />}
            {rejectedPayment ? (
              <Spacing vertical alignItems="flex-start" size="M">
                <Text size="S" weight="bold">
                  {t('payment:label.rejected_paypal_payment')}
                </Text>
                <div className="max-w-md">
                  <Text size="S">{t('payment:label.rejected_paypal_payment_description')}</Text>
                </div>
                <Button
                  variant="primary"
                  fullWidthOnSmall
                  borderRadius="M"
                  mobileBorderRadius="M"
                  onClick={handleRedirectToSearch}
                  text={t('payment:button.use_other_payment_method')}
                  fontSize="S"
                />
              </Spacing>
            ) : (
              <>
                <Text size="S" weight="bold">
                  {t('payment:label.purchase_summary')}:
                </Text>
                <Spacing vertical size="XS">
                  <Text size="S" weight="bold">
                    {t('payment:trip', { context: isRoundTrip ? 'round' : 'one_way' })}
                  </Text>
                  <Text size="XL">
                    {from} - {to}
                  </Text>
                </Spacing>
                {!isOpenTicket && (
                  <Spacing vertical size="XS">
                    <Text size="S" weight="bold">
                      {t('payment:label.departure_date')}
                    </Text>
                    <Text size="L">{departsDisplay}</Text>
                  </Spacing>
                )}
              </>
            )}
          </Spacing>
        </div>

        {!rejectedPayment && (
          <div className="tickets-panel-content right-content">
            <div className="right-content-info">
              <Spacing vertical>
                <Text size="S">{t('purchase:label.no_payment_yet_click_to_finish')}:</Text>
                <Spacing vertical size="S">
                  <Timer variant="payment" remainingTime={timerRemainingTime} />
                  <Button variant="primary" onClick={onConfirm} text={buttonText} />
                </Spacing>
              </Spacing>
            </div>
            <div className="right-content-price">
              <Spacing vertical size="S">
                <Text size="S" textAlign="center" color="grayMedium">
                  {t('payment:label.pending_payment')}:
                </Text>
                <div className="right-content-price-amount text-center">
                  <LocalizedCurrency
                    price={amount}
                    currencyStyle="currency"
                    size="XL"
                    weight="bold"
                  />
                </div>
              </Spacing>
            </div>
          </div>
        )}
      </div>
      {!rejectedPayment && <TimeoutModal />}
    </div>
  );
};

PaypalPaymentInfo.propTypes = {
  amount: PropTypes.number.isRequired,
  confirmPaypalPayment: PropTypes.func.isRequired,
  departs: PropTypes.string.isRequired,
  from: PropTypes.string.isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
  isOpenTicket: PropTypes.bool.isRequired,
  paymentId: PropTypes.number.isRequired,
  to: PropTypes.string.isRequired,
  paymentType: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  timerRemainingTime: PropTypes.number.isRequired,
  paymentStatus: PropTypes.string,
  purchaseState: PropTypes.string,
  purchaseIsExpired: PropTypes.bool,
  paymentIsLoaded: PropTypes.bool,
};

export default PaypalPaymentInfo;
